import React, { forwardRef, useRef, useState } from "react";
import { Tree, UncontrolledTreeEnvironment } from "react-complex-tree";

const TreeEnvironmentWrapper = forwardRef((props, ref) => {
    const {
        dataProvider,
        viewState,
        treeId,
        onSelectItems,
        onDrop,
        renderItem,
        canDragAndDrop = true,
        canReorderItems = true,
        canDropOnFolder = false,
        canDropOnNonFolder = false,
        multiSelect = true,
        onExpandItem,
        onCollapseItem,
    } = props;
    const environmentRef = useRef(null);
    const treeRef = useRef(null);
    const isTreeItemDraggingRef = useRef(false);

    React.useImperativeHandle(ref, () => ({
        expandItem: (itemId) => {
            treeRef.current?.expandItem(itemId);
        },
        collapseItem: (itemId) => {
            treeRef.current?.collapseItem(itemId);
        },
        unselectAllItems: () => {
            treeRef.current?.selectItems([]);
        },
    }));

    // uncontrolled tree 는 key 변경 시 다시 그리기 가능
    const [treeRedrawKey, setTreeRedrawKey] = useState(`${Math.random()}`);
    const redrawTree = () => {
        setTreeRedrawKey(`${Math.random()}`);
    };

    const handleDrop = (dropItems, dropPosition) => {
        isTreeItemDraggingRef.current = false;

        // 드롭 아이템을 선택 상태로 설정
        let selectedItems = dropItems.map((item) => item.index);
        treeRef.current?.selectItems(selectedItems);

        // 트리 다시 그리기 (드롭 전 선택 상태가 화면에 남아있는 현상 수정)
        redrawTree();

        // 전체 트리 상태 가져오기
        const currentItems = dataProvider.data.items;
        const rootItem = "root"; // 트리의 루트 아이템

        // 부모 정보를 식별하기 위한 맵 구성
        const parentMap = {};
        Object.keys(currentItems).forEach((itemId) => {
            const item = currentItems[itemId];
            const children = item.children ?? [];
            children.forEach((childId) => {
                parentMap[childId] = itemId; // 자식 아이템 ID를 키로 사용하여 부모 ID 매핑
            });
        });

        // 트리를 순회하며 모든 노드에 대한 `linearIndex`를 계산
        const assignLinearIndex = (currentItems, rootItem) => {
            let index = 0;
            const linearIndices = {};

            const traverseTree = (itemId) => {
                linearIndices[itemId] = index++;
                const children = currentItems[itemId]?.children ?? [];
                children.forEach(traverseTree);
            };

            traverseTree(rootItem);
            return linearIndices;
        };
        const allLinearIndices = assignLinearIndex(currentItems, rootItem);

        // 모든 노드에 대한 부모 및 순서 정보 수집
        const updatedNodes = Object.keys(currentItems)
            .filter((itemId) => itemId !== rootItem) // `root`를 제외
            .map((itemId) => {
                const parent = parentMap[itemId] || null; // 부모가 없으면 null
                return {
                    id: itemId,
                    parent: parent === rootItem ? null : parent,
                    order: allLinearIndices[itemId], // 0부터 시작하는 순서
                };
            });

        // 서버에 업데이트 요청
        onDrop(updatedNodes);
    };

    return (
        <UncontrolledTreeEnvironment
            key={`${JSON.stringify(viewState)}-${JSON.stringify(dataProvider.data.items)}-${treeRedrawKey}`}
            ref={environmentRef}
            dataProvider={dataProvider}
            viewState={viewState}
            getItemTitle={(item) => item.name}
            canDragAndDrop={canDragAndDrop}
            canReorderItems={canReorderItems}
            canDropOnFolder={canDropOnFolder}
            canDropOnNonFolder={canDropOnNonFolder}
            defaultInteractionMode={{
                mode: "custom",
                createInteractiveElementProps: (item, treeId, actions, renderFlags) => ({
                    onClick: (e) => {
                        actions.focusItem(); // 기본 포커스 동작 유지
                        if (multiSelect) {
                            // 다중 선택 모드
                            if (e.shiftKey) {
                                actions.selectUpTo(true); // Shift 키를 누르면 범위 선택
                            } else {
                                if (renderFlags.isSelected) {
                                    actions.unselectItem(); // 선택 해제
                                } else {
                                    actions.addToSelectedItems(); // 선택 추가
                                }
                            }
                        } else {
                            if (renderFlags.isSelected) {
                                actions.unselectItem(); // 선택 해제
                            } else {
                                actions.selectItem();
                                treeRef.current?.selectItems([item.index]);
                            }
                        }
                    },
                    onFocus: () => {
                        actions.focusItem();
                    },
                    onDragStart: (e) => {
                        isTreeItemDraggingRef.current = true;

                        let selectedItems = [...viewState[treeId].selectedItems];

                        if (multiSelect) {
                            if (!selectedItems.includes(item.index)) {
                                selectedItems.push(item.index);
                            }
                        } else {
                            selectedItems = [item.index];
                        }

                        // 안내 메시지를 담은 임시 드래그 프리뷰 요소를 생성
                        const dragPreview = document.createElement("div");
                        dragPreview.style.position = "absolute";
                        dragPreview.style.top = "-1000px";
                        dragPreview.style.left = "-1000px";
                        dragPreview.style.padding = "8px 12px 8px 24px";
                        dragPreview.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
                        dragPreview.style.color = "#fff";
                        dragPreview.style.fontSize = "14px";
                        dragPreview.style.borderRadius = "4px";
                        dragPreview.innerText = `${selectedItems.length}개 요소를 이동`;

                        // 생성한 요소를 body에 삽입 후 드래그 이미지로 설정
                        document.body.appendChild(dragPreview);
                        e.dataTransfer.setDragImage(dragPreview, 0, 0);

                        // 이미지 설정 직후 임시 요소 제거
                        setTimeout(() => {
                            document.body.removeChild(dragPreview);
                        }, 0);

                        e.dataTransfer.dropEffect = "move";
                        actions.startDragging();
                    },
                    onDragOver: (e) => {
                        e.preventDefault();
                    },
                    onDragEnd: (e) => {
                        isTreeItemDraggingRef.current = false;
                        const selectedItems = viewState[treeId].selectedItems;
                        treeRef.current?.selectItems(selectedItems);
                    },
                    draggable: renderFlags.canDrag && !renderFlags.isRenaming,
                }),
            }}
            onSelectItems={(selectedIds) => {
                if (!isTreeItemDraggingRef.current) {
                    if (multiSelect) {
                        // 다중 선택 모드: 선택된 항목 그대로 전달
                        onSelectItems(selectedIds);
                    } else {
                        const selectedId = selectedIds.length > 0 ? selectedIds[selectedIds.length - 1] : null;
                        onSelectItems(selectedId ? [selectedId] : []);
                    }
                }
            }}
            onExpandItem={(item) => {
                onExpandItem(item);
            }}
            onCollapseItem={(item) => {
                onCollapseItem(item);
            }}
            onDrop={handleDrop}
            renderItem={renderItem}
        >
            <Tree treeId={treeId} rootItem="root" ref={treeRef} />
        </UncontrolledTreeEnvironment>
    );
});

export default TreeEnvironmentWrapper;
